import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DataFormService, FormFieldData } from '../../services/data-form/data-form.service';

@Component({
	selector: 'pcm-data-form',
	templateUrl: './data-form.component.html',
	styleUrls: ['./data-form.component.scss']
})
export class DataFormComponent implements OnChanges {
	@Input() title: string;
	@Input() id: string;

	// config object for this object type (formerly 'dataConfig')
	// contains sub-objects 'fields' (an array of fields) and 'objects' (an array of
	// objects that also contains an arrays of additional fields)
	@Input() config: {
		fields: Array<any>,
		objects: Array<any>
	};

	@Input() includePrimaryFields = true;

	@Input() includeAddOnFields = true;

	// data to fill in fields (formerly 'pageData'). properties correspond to field names in 'config' object
	@Input() data: any;

	// this prop is passed along to the logic that adds ACL to the resulting form data (dataService.applyACL)
	@Input() aclOverwrite = false;

	// an object with properties that can be used as 'options' for select type fields, matching their prop names
	@Input() selectOptionsLookup: any;

	// text or instructions shown at the top of the form (formerly 'helpText')
	@Input() subtitle: string;

	// a list of action buttons at the bottom of the form
	@Input() actionButtons = [
		{
			id: 'submit', // can be left off if it will not be needed by the parent component
			label: 'Submit',
			type: 'primary', // 'primary', 'secondary', 'tertiary', 'danger' // template will assume 'primary' if not specified
			disableOnInvalidForm: true, // template will assume true if not included
			disabledIfPristine: true, // if true, button will not be available if no changes have been made to the form
		}
	];

	// if true, the input "data" property will be merged with the resulting form data when returned
	@Input() mergeOutputWithInput = false;

	// insert any element template above or below the form fields. object: { position: 'top'|'bottom', template: <TemplateRef> }
	@Input() insertTemplates: TemplateRef<any>;

	// pass in a function that will allow interacting with the internal form
	@Input() formSetupHook: Function;

	// in case extra fields need to be considered in the form validity for the sake of enabling/disabling
	// a button, pass in this object like this: { valid: boolean, touched: boolean }
	@Input() additionalFormValidity: any;

	// event emitted when an action button is pressed.
	// this will return an object like this:
	// { id, data }, where 'id' is the id of the action button pressed ('submit' by default) and 'data' is the form data
	@Output() actionButtonPressed: EventEmitter<any> = new EventEmitter();

	// display data is a subset of "data" that is passed in, and is only what is needed to display the form
	displayData: Array<FormFieldData>;

	// form is the FormGroup object that defines the fields in Angular forms
	form: UntypedFormGroup;

	constructor(
		private dataFormService: DataFormService,
		private formBuilder: UntypedFormBuilder
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		// if any changes are detected to inputs, re-generate form
		if (changes['config'] || changes['data'] || changes['selectOptionsLookup']) {
			this.displayData = this.dataFormService.convertDataForForm(
				this.config,
				this.data,
				this.selectOptionsLookup,
				this.includePrimaryFields,
				this.includeAddOnFields);
			this.form = this.formBuilder.group(this.dataFormService.getFieldsForFormBuilder(this.displayData));

			this.dataFormService.subscribeToReactiveFields(this.form, this.displayData);
			if (this.formSetupHook) {
				this.formSetupHook(this.form);
			}
		}
	}

	getFieldsToShow(): Array<FormFieldData> {
		return this.displayData;
	}

	getInvalidText(prop: string): string {
		return this.dataFormService.getInvalidText(this.form, prop);
	}

	handleActionButtonClick(id) {
		const formDataForReturn = this.dataFormService.formatForReturn(
			this.form, this.displayData, this.mergeOutputWithInput, this.data, this.aclOverwrite, this.config);
		this.actionButtonPressed.emit({ id: id, data: formDataForReturn });
	}

	shouldShow(item): boolean {
		// form data for required or only if checks may need data from the form or the data property
		return this.dataFormService.shouldShow(item, this.form, { ...this.data, ...this.form.value });
	}

	/**
	 * A button should be disabled/enabled based on form validation.
	 * However, a button can be marked to never be disabled with the 'disableOnInvalidForm' property
	 * Also, if there are extra fields which aren't part of the FormGroup (like the invoice calendar on contractor)
	 * then the validity of those extra fields can be given in the 'additionalFormValidity' property (valid and touched)
	 * to factor into whether or not a button should be disabled.
	 */
	isActionButtonDisabled(button): boolean {
		const buttonCanBeDisabled = button.disableOnInvalidForm === undefined ? true : button.disableOnInvalidForm;
		const buttonIsDisabledIfPristine = button.disabledIfPristine === undefined ? true: button.disabledIfPristine;
		// these would be default values in case no extra validation is being considered
		let formAndExtrasAreUntouched = this.form.pristine;
		let extrasValid = true;
		// consider extra fields that may be considered outside the form when determining if button is disabled
		if (this.additionalFormValidity) {
			formAndExtrasAreUntouched = this.form.pristine && !this.additionalFormValidity.touched;
			extrasValid = this.additionalFormValidity.valid;
		}
		// disabled if form or extras are invalid, or if both form and extras are untouched. but if button cannot be disabled then ignore.
		return (buttonCanBeDisabled) && (!this.form.valid || (formAndExtrasAreUntouched && buttonIsDisabledIfPristine) || !extrasValid);
	}
}
